import React, { useContext, useEffect } from "react";
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Tooltip,
  IconButton,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import CurrencySelectField from "../../selectField/CurrencySelectField";
import AuthContext from "../../../context/AuthContext";

export const iso_fields = [
  {
    label: "Business Name",
    name: "isoName",
    type: "text",
    shrink: true,
    // maxLength: 16,
    required: true,
    helperText: "DBA (Doing Business As) name",
    isRootLevel: true, // Indica que está en el nivel raíz, no en iso.*
  },
  {
    label: "Email",
    name: "email",
    type: "text",
    shrink: true,
    // maxLength: 32,
    required: true,
    helperText: "Email ISO, also used to send user data",
  },
  {
    label: "Address",
    name: "address",
    type: "text",
    shrink: true,
    maxLength: 32,
    required: true,
    helperText: "Street address of the ISO",
  },
  {
    label: "City",
    name: "city",
    type: "text",
    shrink: true,
    maxLength: 32,
    required: true,
    helperText: "",
  },
  {
    label: "State",
    name: "state",
    type: "text",
    shrink: true,
    // maxLength: 32,
    required: true,
    helperText: "State, Province, Department",
  },
  {
    label: "Postal Code",
    name: "postalCode",
    type: "text",
    shrink: true,
    maxLength: 16,
    required: true,
    helperText: "ZIP or postal code",
  },
  {
    label: "Country Code",
    name: "countryCode",
    type: "text",
    shrink: true,
    // maxLength: 2,
    required: true,
  },
  {
    label: "Processing Transaction Fee",
    name: "amountTxProcessing",
    type: "text",
    shrink: true,
    // maxLength: 16,
    required: true,
    helperText: "",
  },
  {
    label: "MDR Percentage Fee",
    name: "percentTxProcessing",
    type: "text",
    shrink: true,
    // maxLength: 6,
    required: true,
    helperText: "",
  },
];

const IsoFields = ({
  formValues,
  handleChange,
  handleBlur,
  setFieldValue,
  errors,
  touched,
  isEditMode = false,
}) => {
  const { userInfo, acquirerData } = useContext(AuthContext);
  const currencyValue = formValues.iso?.currencyCode || "";
  const currencyError = errors.iso?.currencyCode;
  const currencyTouched = touched.iso?.currencyCode;
  const userRole = userInfo?.["acquirer-rol-data"];
  const isCurrencyDisabled = userRole !== "admin";

  useEffect(() => {
    // Precargar currency al crear solo si el campo viene vacío.
    // En edición respetamos el valor cargado por setIsoValues.
    if (isEditMode) return;
    if (currencyValue) return;
    if (!acquirerData?.currency_code) return;

    setFieldValue("iso.currencyCode", acquirerData.currency_code);
  }, [
    acquirerData?.currency_code,
    currencyValue,
    isEditMode,
    setFieldValue,
  ]);

  return (
    <>
      <Grid item xs={12} sm={3}>
        <CurrencySelectField
          id="iso.currencyCode"
          name="iso.currencyCode"
          fullWidth
          label="Currency"
          value={currencyValue}
          required
          maxLength={3}
          onChange={handleChange}
          onBlur={handleBlur}
          error={currencyError}
          touched={currencyTouched}
          disabled={isCurrencyDisabled}
        />
      </Grid>

      {iso_fields
        .filter((field) => !(isEditMode && field.name === "email"))
        .map((field) => {
          const isRequired =
            typeof field.required === "function"
              ? field.required(formValues)
              : field.required;

          // Obtener el valor actual del campo
          const fieldValue = field.isRootLevel
            ? formValues[field.name] || ""
            : formValues.iso?.[field.name] || "";

          // Determinar el path del campo para errores y touched
          const fieldPath = field.isRootLevel
            ? field.name
            : `iso.${field.name}`;
          const fieldError = fieldPath
            .split(".")
            .reduce((obj, key) => obj?.[key], errors);
          const fieldTouched = fieldPath
            .split(".")
            .reduce((obj, key) => obj?.[key], touched);

          const isError = Boolean(fieldTouched && fieldError);
          const helperText =
            fieldTouched && fieldError ? fieldError : field.helperText || "";

          return (
            <Grid item xs={12} sm={3} key={field.name}>
              {field.type === "select" ? (
                <FormControl fullWidth required={isRequired} error={isError}>
                  <InputLabel id={`${field.name}-label`}>
                    {field.label}
                  </InputLabel>
                  <Select
                    labelId={`${field.name}-label`}
                    name={fieldPath}
                    label={field.label}
                    value={fieldValue}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    {field.options.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{helperText}</FormHelperText>
                </FormControl>
              ) : (
                <TextField
                  label={
                    <>
                      {field.label}
                      {field.helperText && !isError && (
                        <Tooltip title={field.helperText} arrow>
                          <IconButton
                            size="small"
                            sx={{ padding: 0, marginLeft: "4px" }}
                          >
                            <InfoIcon color="primary" fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  }
                  name={fieldPath}
                  value={fieldValue}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  InputLabelProps={{
                    shrink: field.shrink,
                    sx: {
                      "& .MuiFormLabel-asterisk": {
                        color: "red",
                        fontSize: "1.2rem",
                      },
                    },
                  }}
                  inputProps={{ maxLength: field.maxLength }}
                  required={isRequired}
                  type={field.type}
                  error={isError}
                  helperText={helperText}
                  fullWidth
                />
              )}
            </Grid>
          );
        })}
    </>
  );
};

export default IsoFields;
